import { useState, useEffect, useCallback } from 'react';
import { Translate } from 'react-redux-i18n';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPlus } from '@fortawesome/free-solid-svg-icons';
import { getViewEditSearchUrl } from '@helpers/getDashboardRedirectUrl';
import './CompanySearchableDropdown.scss';
import get from 'lodash/get';
import useCountryCodeInfo from '@helpers/customHooks/useCountryCodeInfo';
import { CompanyDetails } from '@customTypes/ItemTypes';
import { CompanyDropdownProps } from '@customTypes/PropTypes';
import useEntitlementsForADuns from '@helpers/customHooks/useEntitlementsForADuns';
import { setIsLoading } from '@redux/slices/appSlice';
import { sendGA4ClickEvent } from '@helpers/gtmUtils';
import debounce from 'lodash/debounce';
import { useAppDispatch } from '@redux/reduxHooks';

const CompanySearchableDropdown = ({ companyData, activeCompany, switchCompany }: CompanyDropdownProps): JSX.Element => {
    const { featuresList } = useCountryCodeInfo();
    const dispatch = useAppDispatch();
    const { isAddCompanyCTAEnabled, addCompanyCTAURL } = featuresList;
    const [dropDownOptionFlag, setDropDownOptionFlag] = useState(false);
    const [companyName, setCompanyName] = useState(activeCompany.summary.businessName);
    const [activeDuns, setActiveDuns] = useState(activeCompany.profileDetails.dunsNumber);
    const [searchTerm, setSearchTerm] = useState(''); // Added searchTerm state
    const [isSearchActive, setIsSearchActive] = useState(false);
    useEntitlementsForADuns(activeDuns);

    useEffect(() => {
        const closeDropDown = () => {
            setDropDownOptionFlag(false);
            setIsSearchActive(false); // Close search input when dropdown closes
        };
        window.addEventListener('click', closeDropDown, false);
        return () => {
            window.removeEventListener('click', closeDropDown, false);
        };
    }, []);

    useEffect(() => {
        setCompanyName(activeCompany.summary.businessName);
        setActiveDuns(activeCompany.profileDetails.dunsNumber);
    }, [activeCompany]);

    const handleCompanyClick = useCallback(
        debounce((company: CompanyDetails) => {
            const companyIndex = companyData.findIndex(
                (c) => c.profileDetails.dunsNumber === company.profileDetails.dunsNumber
            );
            if (companyIndex !== -1) {
                switchCompany(companyIndex);
            }
            setDropDownOptionFlag(false);
            setIsSearchActive(false);
            sendGA4ClickEvent('linkTrackingEvent', {
                linkTrackingName: 'changeCompanyDropdownClick',
            });
        }, 300),
        [companyData, switchCompany, dispatch]
    );

    // Filtered company data based on search input
    const filteredCompanies = companyData.filter((company) => company.summary?.businessName?.toLowerCase()?.includes(searchTerm.toLowerCase()));

    const options = filteredCompanies.map((company: CompanyDetails, index: number) => {
        const mapDetails = company.mapDetails;
        let address = (mapDetails.buildingName != null) ? `${mapDetails.buildingName},` : '';
        address += (mapDetails.streetAddress != null) ? ` ${mapDetails.streetAddress}` : '';
        address += (mapDetails.town != null) ? `, ${mapDetails.town}` : '';
        address += (mapDetails.territory != null) ? `, ${mapDetails.territory}` : '';

        const activeCompanyName = get(activeCompany, 'summary.businessName', '');
        const companyName = get(company, 'summary.businessName', ' ');
        const dunsNumber = get(company, 'profileDetails.dunsNumber', '--');
        address = dunsNumber ? address : '';
        const fullAddress = (address.trim() !== '') ? address : '  ';

        return (
            <div
                data-testid="select-option"
                className={`dropdown-item ${(activeCompanyName === companyName && activeDuns === dunsNumber) ? 'active' : ''}`}
                onClick={(event) => {
                    event.stopPropagation();
                    handleCompanyClick(company); // Pass the clicked company
                }}
                key={dunsNumber}
            >
                <span className="dropdown-active">
                    {(activeCompanyName === companyName && activeDuns === dunsNumber) && <FontAwesomeIcon icon={faEye} />}
                </span>
                <span className="company" title={companyName}>{companyName}</span>
                <span className="details">
                    <div className="address" title="address">{fullAddress}</div>
                </span>
            </div>
        );
    });

    return (
        <div className="company-dropdown" data-testid="company-dropdown">
            <button
                type="button"
                className="dropdown-toggle btn btn-primary"
                onClick={(event) => {
                    event.stopPropagation();
                    setDropDownOptionFlag(!dropDownOptionFlag);
                    setIsSearchActive(!isSearchActive); // Toggle search input visibility
                }}
            >
                {isSearchActive ? (
                    <input
                        type="text"
                        className="dropdown-search"
                        placeholder="Search company..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing
                        autoFocus // Focus on input when active
                    />
                ) : (
                    <div className="active-label">{companyName}</div>
                )}
            </button>
            {dropDownOptionFlag && (
                <div className="dropdown-menu" id="dropdown-menu">
                    <div className="dropdown-menu-scroll">
                        {options}
                    </div>
                    {isAddCompanyCTAEnabled && (
                        <div
                            className="dropdown-item add-company"
                            key="addCompany"
                            onClick={(event) => {
                                event.stopPropagation();
                                setDropDownOptionFlag(false);
                                setTimeout(() => { window.open('/logout', '_self'); }, 1000);
                            }}
                        >
                            <a
                                href={getViewEditSearchUrl(addCompanyCTAURL)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => sendGA4ClickEvent('linkTrackingEvent', { linkTrackingName: 'addCompanyClick' })}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                <Translate value="add-a-company" />
                            </a>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CompanySearchableDropdown;
